'use client'

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { YMarketOrder } from '@/lib/types'
import type { BuyerInfo } from '@/lib/orders-store'
import { displayPersonName, formatOrderCustomerName, getCity, orderShipment } from '@/lib/order-helpers'
import {
  MISSION_STAGE_META,
  missionRecipientProximity,
  type AuthoritativeMissionControlSummary,
  type MissionControlFlowSummary,
  type MissionControlOrder,
  type MissionControlStage,
} from '@/lib/order-mission-control'
import { formatRelativeTime } from '@/lib/utils'
import {
  DEFAULT_SHIPPING_ORIGIN,
  dispatchWindowLabel,
  nextDispatchWindow,
  shippingTimezoneLabel,
  type ShippingOriginSchedule,
} from '@/lib/shipping-schedule'
import { LocalDateTime } from '@/components/ui/LocalDateTime'
import { RefreshIcon } from '@/components/icons'
import { OrderCard } from '@/components/orders/OrderCard'
import { OrderCardIconButton } from '@/components/orders/OrderCardChrome'
import { ValueOrReason } from '@/components/warehouse/primitives'
import { paymentTotal } from '@/lib/order-helpers'
import {
  fetchCdekPrintDocuments,
  fetchCdekPrintPdf,
  fetchYandexPrintDocuments,
  fetchYandexPrintPdf,
  packOrder,
  CdekPrintRefusalError,
  type CdekPrintBundle,
  YandexPrintRefusalError,
  type YandexPrintBundle,
} from '@/lib/api/client/orders'
import {
  cdekPrintAgentFailureMessage,
  cdekPrintAgentStatusFailureMessage,
  createCdekPrintRequestId,
  CdekPrintAgentError,
  fetchCdekPrintAgentStatus,
  isCdekPrintAgentEnabled,
  printCdekDocumentWithAgent,
  type CdekPrintAgentStatus,
  type CdekPrintKind,
} from '@/lib/cdek-print-agent'
import {
  CdekBrowserPrintError,
  printCdekBrowserPages,
  renderCdekPdfPages,
} from '@/lib/cdek-print-fallback'
import {
  currentMonthKeyInRiga,
  MonthlyReturnState,
  returnsForMonth,
  type MonthlyReturnOrder,
} from '@/lib/monthly-order-returns'

interface Props {
  summary: AuthoritativeMissionControlSummary | null
  orders: YMarketOrder[]
  buyerMap: Record<number, BuyerInfo>
  loading: boolean
  ordersComplete?: boolean
  error?: string
  lastFetchedAt?: number | null
  onRefresh?: () => void
  onSelect: (order: YMarketOrder) => void
  compact?: boolean
  shippingOrigin?: ShippingOriginSchedule
}

const STAGE_TONES: Record<MissionControlStage, { dot: string; surface: string; text: string }> = {
  delivery_issue: { dot: 'bg-st-unpaid', surface: 'bg-st-unpaid/7', text: 'text-st-unpaid' },
  returning: { dot: 'bg-st-unpaid', surface: 'bg-st-unpaid/7', text: 'text-st-unpaid' },
  out_for_delivery: { dot: 'bg-gold-deep', surface: 'bg-gold/10', text: 'text-gold-deep' },
  ready_for_pickup: { dot: 'bg-st-delivered', surface: 'bg-st-delivered/8', text: 'text-st-delivered' },
  in_transit: { dot: 'bg-st-delivery', surface: 'bg-st-delivery/8', text: 'text-st-delivery' },
  preparing: { dot: 'bg-st-processing', surface: 'bg-st-processing/9', text: 'text-st-processing' },
  processing: { dot: 'bg-ink-faint', surface: 'bg-bone/60', text: 'text-ink-muted' },
  delivery_pending: { dot: 'bg-st-pickup', surface: 'bg-st-pickup/8', text: 'text-st-pickup' },
  awaiting_payment: { dot: 'bg-gold', surface: 'bg-gold/8', text: 'text-gold-deep' },
}

function money(value: number): string {
  return `${Math.round(value).toLocaleString('ru-RU')}\u2009₽`
}

function flowAmount(flow: MissionControlFlowSummary): string {
  const amount = money(flow.knownAmount)
  return flow.unknownAmountCount > 0
    ? `${amount} + ${flow.unknownAmountCount} без суммы`
    : amount
}

function knownAmount(items: MissionControlOrder[]): number {
  return items.reduce((sum, item) => sum + (item.amount ?? 0), 0)
}

function orderWord(count: number): string {
  const lastTwo = count % 100
  if (lastTwo >= 11 && lastTwo <= 14) return 'заказов'
  const last = count % 10
  if (last === 1) return 'заказ'
  if (last >= 2 && last <= 4) return 'заказа'
  return 'заказов'
}

type MarketDispatchDay = {
  key: string
  label: string | null
  items: MissionControlOrder[]
}

// Shipping two dispatch days together is a marketplace penalty, so the operator
// needs one section per day rather than one list ordered by when orders arrived.
function marketDispatchDays(items: MissionControlOrder[]): MarketDispatchDay[] {
  const days = new Map<string, MarketDispatchDay>()
  for (const item of items) {
    const shipmentDate = item.order.delivery?.shipment?.shipmentDate
    const key = shipmentDate?.slice(0, 10) || ''
    const day = days.get(key)
    if (day) {
      day.items.push(item)
      continue
    }
    days.set(key, { key, label: dateLabel(shipmentDate), items: [item] })
  }
  // An order with no shipment date cannot be scheduled against the others, so it
  // trails them instead of claiming the earliest slot an empty key would sort to.
  return [...days.values()].sort((left, right) => {
    if (!left.key) return 1
    if (!right.key) return -1
    return left.key.localeCompare(right.key)
  })
}

function dateLabel(value?: string): string | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '')
}

function timeLabel(value?: string): string | null {
  const match = value?.trim().match(/^(\d{1,2}):(\d{2})/)
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : null
}

function cdekDeliveryTiming(item: MissionControlOrder): { label: string; value: string } | null {
  const shipment = orderShipment(item.order)
  const agreementDate = dateLabel(shipment?.agreement?.date)
  const timeFrom = timeLabel(shipment?.agreement?.timeFrom)
  const timeTo = timeLabel(shipment?.agreement?.timeTo)
  const interval = timeFrom && timeTo
    ? `${timeFrom}–${timeTo}`
    : timeFrom ? `с ${timeFrom}` : timeTo ? `до ${timeTo}` : null
  const agreement = [agreementDate, interval].filter(Boolean).join(', ')

  if (agreement) return { label: 'Согласовано со СДЭК', value: agreement }
  const planned = dateLabel(item.plannedDeliveryDate)
  if (!planned) return null
  return item.stage === 'out_for_delivery'
    ? { label: 'Доставка курьером', value: planned }
    : { label: 'План доставки', value: planned }
}

const MONTHLY_RETURN_META: Record<MonthlyReturnState, { label: string; tone: string; dot: string }> = {
  [MonthlyReturnState.Returning]: {
    label: 'Возвращается на наш склад',
    tone: 'text-st-unpaid',
    dot: 'bg-st-unpaid',
  },
  [MonthlyReturnState.Completed]: {
    label: 'Получен на нашем складе',
    tone: 'text-st-delivered',
    dot: 'bg-st-delivered',
  },
  [MonthlyReturnState.Failed]: {
    label: 'Возврат не доставлен · снова у покупателя',
    tone: 'text-st-unpaid',
    dot: 'bg-st-unpaid',
  },
}

function monthTitle(monthKey: string): string {
  const date = new Date(`${monthKey}-01T12:00:00Z`)
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Riga',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

function MonthlyReturnsDialog({
  items,
  monthKey,
  buyerMap,
  complete,
  onClose,
  onSelect,
}: {
  items: MonthlyReturnOrder[]
  monthKey: string
  buyerMap: Record<number, BuyerInfo>
  complete: boolean
  onClose: () => void
  onSelect: (order: YMarketOrder) => void
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-onyx/30 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="monthly-returns-title"
        className="flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-ivory shadow-2xl ring-1 ring-rule-strong sm:max-w-[820px] sm:rounded-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-rule px-5 py-4 sm:px-6">
          <div>
            <div className="eyebrow text-st-unpaid">Отмены Маркета и обратная логистика</div>
            <h2 id="monthly-returns-title" className="mt-1 text-[22px] font-semibold text-ink">
              Возвраты за {monthTitle(monthKey)}
            </h2>
            <p className="mt-1 text-[12px] text-ink-muted">
              {items.length} {orderWord(items.length)} с отдельной обратной накладной СДЭК
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть список возвратов"
            className="grid size-10 shrink-0 place-items-center rounded-full text-[24px] text-ink-muted hover:bg-bone hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          >
            ×
          </button>
        </header>

        {!complete && (
          <div className="border-b border-gold/25 bg-gold/7 px-5 py-2.5 text-[11px] text-gold-deep sm:px-6">
            Показаны обратные накладные из уже загруженных страниц. Остальные заказы ещё подгружаются.
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[env(safe-area-inset-bottom)] sm:px-6">
          {items.length > 0 ? items.map(({ order, state, occurredAt }) => {
            const meta = MONTHLY_RETURN_META[state]
            const buyer = displayPersonName(order.customerStats?.name || formatOrderCustomerName(order, buyerMap[order.orderId] || null))
            const city = order.customerStats?.city || getCity(order)
            const product = order.items?.[0]?.offerName || order.items?.[0]?.offerId || `Заказ №${order.orderId}`
            const amount = paymentTotal(order)
            return (
              <button
                key={order.orderId}
                type="button"
                onClick={() => {
                  onClose()
                  onSelect(order)
                }}
                className="w-full cursor-pointer border-b border-rule/80 px-1 py-4 text-left hover:bg-bone/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className={`flex min-w-0 items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.07em] ${meta.tone}`}>
                    <span className={`size-2 shrink-0 rounded-full ${meta.dot}`} />
                    <span className="truncate">{meta.label}</span>
                  </span>
                  <span className="shrink-0 text-[13px] font-semibold tab-nums text-ink">
                    {amount === null ? 'Сумма —' : money(amount)}
                  </span>
                </div>
                <div className="mt-2 truncate text-[14px] font-semibold text-ink">{product}</div>
                <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[11px] text-ink-muted">
                  <span>{[buyer !== '—' ? buyer : '', city].filter(Boolean).join(' · ') || `Заказ №${order.orderId}`}</span>
                  <span className="text-ink-faint">·</span>
                  <LocalDateTime value={occurredAt} prefix="Обновление возврата: " />
                </div>
              </button>
            )
          }) : (
            <div className="px-5 py-12 text-center">
              <div className="text-[14px] font-medium text-ink">В этом месяце возвратов не найдено</div>
              <div className="mt-1 text-[12px] text-ink-muted">Обычный статус «Не вручен» без обратной накладной сюда не входит.</div>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

type PrintProvider = 'cdek' | 'ymarket'
type PrintBundle = CdekPrintBundle | YandexPrintBundle

function revokePdfUrls(urls: { a6: string; a4?: string } | null): void {
  if (!urls || typeof URL.revokeObjectURL !== 'function') return
  URL.revokeObjectURL(urls.a6)
  if (urls.a4) URL.revokeObjectURL(urls.a4)
}

// The tab promised «3 страницы» whether or not any document existed, so a dialog
// whose body said the documents were not ready still offered to print three.
function pageWord(count: number): string {
  const tail = count % 100 >= 11 && count % 100 <= 14 ? 0 : count % 10
  if (tail === 1) return `${count} страница`
  if (tail >= 2 && tail <= 4) return `${count} страницы`
  return `${count} страниц`
}


function PrintViewer({
  orderId,
  provider,
  documents,
  pdfUrls,
  pdfBlobs,
  loading,
  error,
  missingFields,
  packing,
  onClose,
  onPack,
  onRetry,
}: {
  orderId: number
  provider: PrintProvider
  documents: PrintBundle | null
  pdfUrls: { a6: string; a4?: string } | null
  pdfBlobs: { a6: Blob; a4?: Blob } | null
  loading: boolean
  error: string | null
  missingFields: readonly string[]
  packing: boolean
  onClose: () => void
  onPack?: () => Promise<void>
  onRetry: () => Promise<void>
}) {
  const [step, setStep] = useState<'a6' | 'a4'>('a6')
  const [readySource, setReadySource] = useState<string | null>(null)
  const [agentEnabled] = useState(isCdekPrintAgentEnabled)
  const [agentStatus, setAgentStatus] = useState<CdekPrintAgentStatus | null>(null)
  const [agentChecking, setAgentChecking] = useState(agentEnabled)
  const [agentNotice, setAgentNotice] = useState<string | null>(null)
  const [printingStep, setPrintingStep] = useState<CdekPrintKind | null>(null)
  const [unknownPrintStep, setUnknownPrintStep] = useState<CdekPrintKind | null>(null)
  const [agentRequestIds, setAgentRequestIds] = useState<Partial<Record<CdekPrintKind, string>>>({})
  const [fallbackPages, setFallbackPages] = useState<Partial<Record<CdekPrintKind, readonly string[]>>>({})
  const frameRef = useRef<HTMLIFrameElement | null>(null)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  useEffect(() => {
    if (!agentEnabled) return
    let cancelled = false
    setAgentChecking(true)
    setAgentStatus(null)
    setAgentNotice(null)
    void fetchCdekPrintAgentStatus()
      .then((status) => {
        if (cancelled) return
        setAgentStatus(status)
      })
      .catch(() => {
        if (cancelled) return
        setAgentStatus(null)
        setAgentNotice(cdekPrintAgentStatusFailureMessage())
      })
      .finally(() => {
        if (!cancelled) setAgentChecking(false)
      })
    return () => {
      cancelled = true
    }
  }, [orderId, agentEnabled])

  const isCdek = provider === 'cdek'
  const currentDocument = step === 'a6'
    ? documents?.a6
    : documents && 'a4' in documents ? documents.a4 : undefined
  const currentSource = step === 'a6' ? pdfUrls?.a6 || null : pdfUrls?.a4 || null
  const documentReady = currentSource !== null && readySource === currentSource
  const browserPrint = async (kind: CdekPrintKind, blob: Blob) => {
    const cachedPages = fallbackPages[kind]
    const pages = cachedPages || await renderCdekPdfPages(blob)
    if (!cachedPages) {
      setFallbackPages((current) => ({ ...current, [kind]: pages }))
    }
    await printCdekBrowserPages(kind, pages)
  }

  const printCurrent = async () => {
    if (!documentReady || agentChecking || printingStep !== null) return
    const frame = frameRef.current
    if (!frame || frame.src !== currentSource) return
    if (!agentEnabled) {
      frame.contentWindow?.focus()
      frame.contentWindow?.print()
      return
    }
    const blob = pdfBlobs?.[step]
    if (!blob) return
    setPrintingStep(step)
    setAgentNotice(null)
    try {
      if (agentStatus) {
        const requestId = agentRequestIds[step] || createCdekPrintRequestId()
        setAgentRequestIds((current) => ({ ...current, [step]: requestId }))
        await printCdekDocumentWithAgent(step, blob, requestId)
        setAgentRequestIds((current) => {
          const next = { ...current }
          delete next[step]
          return next
        })
        setUnknownPrintStep(null)
      } else {
        await browserPrint(step, blob)
      }
    } catch (caught) {
      if (agentStatus) {
        const agentError = caught instanceof CdekPrintAgentError ? caught : null
        if (!agentError || agentError.kind === 'unknown' || agentError.kind === 'invalid_response') {
          setUnknownPrintStep(step)
          setAgentNotice(agentError
            ? cdekPrintAgentFailureMessage(agentError)
            : 'Не удалось подтвердить печать — проверьте принтер.')
          return
        }
        setAgentRequestIds((current) => {
          const next = { ...current }
          delete next[step]
          return next
        })
        setAgentNotice(cdekPrintAgentFailureMessage(agentError))
        try {
          await browserPrint(step, blob)
          setUnknownPrintStep(null)
        } catch (fallbackCaught) {
          setAgentNotice(fallbackCaught instanceof Error
            ? fallbackCaught.message
            : 'Не удалось открыть печать в браузере.')
          if (fallbackCaught instanceof CdekBrowserPrintError) {
            frame.contentWindow?.focus()
            frame.contentWindow?.print()
          }
        }
      } else if (!(caught instanceof CdekBrowserPrintError)) {
        setAgentNotice(caught instanceof Error
          ? caught.message
          : 'Не удалось открыть печать в браузере.')
      } else if (!agentStatus && caught instanceof CdekBrowserPrintError) {
        setAgentNotice(`${caught.message} Открываем просмотр PDF.`)
        frame.contentWindow?.focus()
        frame.contentWindow?.print()
      }
    } finally {
      setPrintingStep(null)
    }
  }

  const printCurrentInBrowser = async () => {
    if (!documentReady || printingStep !== null) return
    const blob = pdfBlobs?.[step]
    if (!blob) return
    setPrintingStep(step)
    try {
      await browserPrint(step, blob)
      setUnknownPrintStep(null)
      setAgentNotice(null)
    } catch (caught) {
      setAgentNotice(caught instanceof Error ? caught.message : 'Не удалось открыть печать в браузере.')
      if (caught instanceof CdekBrowserPrintError) {
        frameRef.current?.contentWindow?.focus()
        frameRef.current?.contentWindow?.print()
      }
    } finally {
      setPrintingStep(null)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-onyx/35 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={isCdek ? 'cdek-print-title' : 'ymarket-print-title'}
        className="flex max-h-[94dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-ivory shadow-2xl ring-1 ring-rule-strong sm:max-w-[900px] sm:rounded-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-rule px-5 py-4 sm:px-6">
          <div>
            <div className="eyebrow text-gold-deep">{isCdek ? 'Печать отправления СДЭК' : 'Печать отправления Маркета'}</div>
            <h2 id={isCdek ? 'cdek-print-title' : 'ymarket-print-title'} className="mt-1 text-[22px] font-semibold text-ink">
              Заказ №{orderId}
            </h2>
            <p className="mt-1 text-[12px] text-ink-muted">
              {isCdek
                ? `Два документа для двух принтеров · ${step === 'a6' ? 'этикетка и сертификат A6' : 'заказная форма A4'}`
                : 'Один документ для принтера Datamax · этикетка и сертификат A6'}
              {currentDocument ? ` · ${currentDocument.widthPoints} × ${currentDocument.heightPoints} pt` : ''}
            </p>
            {agentEnabled && (
              <p data-testid="cdek-print-agent-status" className="mt-1 text-[11px] text-ink-muted">
                {agentChecking
                  ? 'Проверяем локальный агент печати…'
                  : agentStatus
                    ? isCdek
                      ? `A6 → ${agentStatus.targets.a6.printerName} · A4 → ${agentStatus.targets.a4.printerName}`
                      : `A6 → ${agentStatus.targets.a6.printerName}`
                    : 'Локальный агент печати недоступен. Используем печать в браузере.'}
              </p>
            )}
            {agentNotice && (
              <p role="status" className="mt-1 max-w-[640px] text-[11px] text-st-unpaid">
                {agentNotice}
              </p>
            )}
            {unknownPrintStep === step && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void printCurrent()}
                  disabled={printingStep !== null}
                  className="min-h-8 rounded-md border border-rule-strong bg-paper px-2.5 text-[11px] font-semibold text-ink-2 hover:border-gold hover:text-gold-deep disabled:cursor-wait disabled:opacity-45"
                >
                  Повторить через агент
                </button>
                <button
                  type="button"
                  onClick={() => void printCurrentInBrowser()}
                  disabled={printingStep !== null}
                  className="min-h-8 rounded-md border border-rule-strong bg-paper px-2.5 text-[11px] font-semibold text-ink-2 hover:border-gold hover:text-gold-deep disabled:cursor-wait disabled:opacity-45"
                >
                  Печать в браузере
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть печать"
            className="grid size-10 shrink-0 place-items-center rounded-full text-[24px] text-ink-muted hover:bg-bone hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          >
            ×
          </button>
        </header>

        <div className="flex flex-wrap gap-2 border-b border-rule px-5 py-3 sm:px-6">
          <button
            type="button"
            aria-pressed={step === 'a6'}
            onClick={() => setStep('a6')}
            className={`min-h-9 rounded-full px-3 text-[11px] font-semibold ${step === 'a6' ? 'bg-gold-deep text-ivory' : 'bg-bone text-ink-muted'}`}
          >
            {documents?.a6.pageCount ? `A6 · ${pageWord(documents.a6.pageCount)}` : 'A6'}
          </button>
          {isCdek && documents && 'a4' in documents ? (
            <button
              type="button"
              aria-pressed={step === 'a4'}
              onClick={() => setStep('a4')}
              className={`min-h-9 rounded-full px-3 text-[11px] font-semibold ${step === 'a4' ? 'bg-gold-deep text-ivory' : 'bg-bone text-ink-muted'}`}
            >
              {`A4 · ${pageWord(documents.a4.pageCount)}`}
            </button>
          ) : null}
        </div>

        {loading ? (
          <div className="grid min-h-[360px] place-items-center px-5 py-12 text-[13px] text-ink-muted" role="status">
            {isCdek ? 'Загружаем документы СДЭК…' : 'Загружаем этикетки Маркета…'}
          </div>
        ) : error ? (
          <div
            className="grid min-h-[220px] place-items-center gap-3 px-5 py-12 text-center"
            role="alert"
          >
            <p className="text-[13px] font-semibold text-ink">{isCdek ? 'Документы СДЭК не готовы.' : 'Документы Маркета не готовы.'}</p>
            <p
              data-testid={isCdek ? 'cdek-print-refusal-detail' : 'ymarket-print-refusal-detail'}
              className="max-w-[520px] text-[13px] text-st-unpaid"
            >
              {error}
            </p>
            {missingFields.length > 0 && (
              <ul
                aria-label="Поля, которых не хватает для бирки"
                data-testid={isCdek ? 'cdek-print-missing-fields' : 'ymarket-print-missing-fields'}
                className="flex max-w-[520px] flex-wrap justify-center gap-1.5"
              >
                {missingFields.map((field) => (
                  <li
                    key={field}
                    className="rounded-full bg-bone px-2.5 py-1 text-[12px] font-semibold text-ink"
                  >
                    {field}
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => void onRetry()}
              className="min-h-9 rounded-md border border-rule-strong bg-paper px-3 text-[11px] font-semibold text-ink-2 transition-colors hover:border-gold hover:text-gold-deep"
            >
              Повторить проверку
            </button>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto bg-bone/35 p-3 sm:p-5">
            <div className="mx-auto flex min-h-[420px] max-w-[720px] justify-center bg-white shadow-sm ring-1 ring-rule">
              <iframe
                key={currentSource || step}
                ref={frameRef}
                title={step === 'a6'
                  ? `Документ A6 ${isCdek ? 'СДЭК' : 'Маркета'}`
                  : 'Документ A4 СДЭК'}
                src={currentSource || undefined}
                data-page-count={currentDocument?.pageCount ?? undefined}
                onLoad={() => setReadySource(currentSource)}
                className="h-[min(68dvh,720px)] w-full"
              />
            </div>
          </div>
        )}

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-rule px-5 py-4 sm:px-6">
          <button
            type="button"
            onClick={() => void printCurrent()}
            disabled={loading || Boolean(error) || !documentReady || agentChecking || printingStep !== null}
            title={agentStatus ? `${agentStatus.targets[step].printerName} · ${agentStatus.targets[step].media}` : undefined}
            className="min-h-9 rounded-md border border-rule-strong bg-paper px-3 text-[11px] font-semibold text-ink-2 transition-colors hover:border-gold hover:text-gold-deep disabled:cursor-not-allowed disabled:opacity-45"
          >
            Печать {step.toUpperCase()}
          </button>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => void onRetry()}
              disabled={loading}
              className="min-h-9 rounded-md px-3 text-[11px] font-medium text-ink-muted hover:bg-bone hover:text-ink disabled:cursor-wait disabled:opacity-45"
            >
              Повторить проверку
            </button>
            <button
              type="button"
              onClick={onClose}
              className="min-h-9 rounded-md px-3 text-[11px] font-medium text-ink-muted hover:bg-bone hover:text-ink"
            >
              Закрыть
            </button>
            {isCdek && step === 'a6' ? (
              <button
                type="button"
                onClick={() => setStep('a4')}
                disabled={loading || Boolean(error) || !pdfUrls?.a4}
                className="min-h-9 rounded-md bg-gold-deep px-3 text-[11px] font-semibold text-ivory hover:bg-gold disabled:cursor-not-allowed disabled:opacity-45"
              >
                Далее: A4
              </button>
            ) : isCdek ? (
              <button
                type="button"
                onClick={() => void onPack?.()}
                disabled={loading || Boolean(error) || packing}
                className="min-h-9 rounded-md bg-gold-deep px-3 text-[11px] font-semibold text-ivory hover:bg-gold disabled:cursor-wait disabled:opacity-45"
              >
                {packing ? 'Сохраняем…' : 'Упаковано · следующий заказ'}
              </button>
            ) : null}
          </div>
        </footer>
      </section>
    </div>
  )
}

function amountLabel(item: MissionControlOrder): string {
  return item.amount === null ? 'Сумма —' : money(item.amount)
}

function orderPlace(buyer: string, city: string): string | null {
  return [buyer !== '—' ? buyer : null, city || null].filter((part): part is string => part !== null).join(' · ') || null
}


function DispatchCard({
  item,
  buyerMap,
  onSelect,
  dispatchDate,
  dispatchLabel = 'Отправка',
  onPrint,
  printing = false,
  packed = false,
}: {
  item: MissionControlOrder
  buyerMap: Record<number, BuyerInfo>
  onSelect: (order: YMarketOrder) => void
  dispatchDate?: string
  dispatchLabel?: string
  onPrint?: (orderId: number) => void
  printing?: boolean
  packed?: boolean
}) {
  const order = item.order
  const buyer = displayPersonName(order.customerStats?.name || formatOrderCustomerName(order, buyerMap[order.orderId] || null))
  const city = order.customerStats?.city || getCity(order)
  const deliveryTiming = cdekDeliveryTiming(item)
  // Both queues have a provider-owned label flow; only the CDEK queue can pack here.
  const canPrint = onPrint !== undefined

  return (
    <OrderCard
      order={order}
      stage={item.stage}
      toneClass={STAGE_TONES[item.stage].text}
      stateLabel={item.statusName || MISSION_STAGE_META[item.stage].shortLabel}
      amountLabel={amountLabel(item)}
      waybill={shipmentWaybill(item)}
      packed={packed}
      onOpen={() => onSelect(order)}
      details={{
        place: orderPlace(buyer, city),
        createdAt: order.creationDate,
        timing: deliveryTiming
          ? `${deliveryTiming.label}: ${deliveryTiming.value}`
          : dispatchDate ? `${dispatchLabel}: ${dateLabel(dispatchDate)}` : null,
      }}
      action={canPrint ? (
        <OrderCardIconButton
          label={`Печать документов для заказа №${order.orderId}`}
          title="Печать документов"
          onClick={() => onPrint?.(order.orderId)}
          disabled={printing}
        >
          {printing ? (
            <span className="size-3.5 animate-spin rounded-full border-2 border-rule-strong border-t-gold-deep motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 3h12v18l-2.5-1.5L13 21l-2.5-1.5L8 21l-2-1.5z" />
              <path d="M9 8h6M9 12h6M9 16h3" />
            </svg>
          )}
        </OrderCardIconButton>
      ) : null}
    />
  )
}

function normalizedSearchValue(value: string): string {
  return value.trim().toLocaleLowerCase('ru-RU').replace(/\s+/g, ' ')
}

function ShipmentSearch({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="relative">
      <label htmlFor="mission-shipment-search" className="sr-only">
        Поиск по всем полям заказов в движении
      </label>
      <span
        className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-ink-faint"
        aria-hidden="true"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <circle cx="11" cy="11" r="7" />
          <path d="m16.5 16.5 4 4" />
        </svg>
      </span>
      <input
        id="mission-shipment-search"
        type="text"
        role="searchbox"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Номер, накладная, город, покупатель, товар, артикул или статус"
        autoComplete="off"
        enterKeyHint="search"
        aria-controls="mission-shipment-results"
        className="min-h-11 w-full appearance-none rounded-lg border border-rule-strong bg-paper py-2.5 pl-10 pr-11 text-[13px] text-ink outline-none transition-colors placeholder:text-ink-faint hover:border-gold/60 focus:border-gold-deep focus:ring-2 focus:ring-gold/20"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Очистить поиск"
          className="absolute inset-y-1.5 right-1.5 flex aspect-square items-center justify-center rounded-md text-[18px] leading-none text-ink-faint transition-colors hover:bg-bone hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
        >
          ×
        </button>
      ) : null}
    </div>
  )
}

function shipmentWaybill(item: MissionControlOrder): string {
  return item.cdekNumber
    || orderShipment(item.order)?.cdekNumber
    || item.order.delivery?.tracks?.[0]?.trackCode
    || ''
}

function shipmentBuyer(item: MissionControlOrder, buyerMap: Record<number, BuyerInfo>): string {
  return displayPersonName(item.order.customerStats?.name
    || formatOrderCustomerName(item.order, buyerMap[item.order.orderId] || null))
}

function shipmentCity(item: MissionControlOrder): string {
  return item.order.customerStats?.city || getCity(item.order)
}

function shipmentStateLabel(item: MissionControlOrder): string {
  if (item.stage === 'delivery_issue') return 'Требует внимания'
  if (item.stage === 'returning') {
    const hasCdekWaybill = item.cdekNumber || orderShipment(item.order)?.cdekNumber
    if (item.order.programType === 'FBS' && !hasCdekWaybill) {
      return item.statusName || 'Возвращается к нам'
    }
    return 'Возвращается к нам'
  }
  if (item.stage === 'out_for_delivery') return 'У курьера'
  return MISSION_STAGE_META[item.stage].label
}

function shipmentSearchValue(
  item: MissionControlOrder,
  buyerMap: Record<number, BuyerInfo>,
): string {
  const timing = cdekDeliveryTiming(item)
  const fields = [
    item.order.orderId,
    shipmentWaybill(item),
    shipmentCity(item),
    shipmentBuyer(item, buyerMap),
    shipmentStateLabel(item),
    item.statusName,
    MISSION_STAGE_META[item.stage].label,
    item.amount,
    item.amount === null ? 'без суммы' : money(item.amount),
    item.order.creationDate,
    dateLabel(item.order.creationDate),
    timing?.label,
    timing?.value,
    ...(item.order.items?.flatMap((product) => [product.offerName, product.offerId]) ?? []),
  ]
  return normalizedSearchValue(fields.filter((value): value is string | number => (
    typeof value === 'number' || (typeof value === 'string' && value.length > 0)
  )).join(' '))
}

function ShipmentLine({
  item,
  buyerMap,
  onSelect,
}: {
  item: MissionControlOrder
  buyerMap: Record<number, BuyerInfo>
  onSelect: (order: YMarketOrder) => void
}) {
  const order = item.order
  const buyer = shipmentBuyer(item, buyerMap)
  const city = shipmentCity(item)
  const waybill = shipmentWaybill(item)
  const deliveryTiming = cdekDeliveryTiming(item)
  const stateLabel = shipmentStateLabel(item)
  const statusLabel = item.statusName || MISSION_STAGE_META[item.stage].shortLabel
  const detail = statusLabel !== stateLabel ? statusLabel : undefined

  return (
    <OrderCard
      order={order}
      stage={item.stage}
      toneClass={STAGE_TONES[item.stage].text}
      stateLabel={stateLabel}
      stateDetail={detail}
      amountLabel={amountLabel(item)}
      waybill={waybill}
      alert={item.stage === 'delivery_issue'}
      onOpen={() => onSelect(order)}
      details={{
        place: orderPlace(buyer, city),
        createdAt: order.creationDate,
        note: detail,
        timing: deliveryTiming ? `${deliveryTiming.label}: ${deliveryTiming.value}` : null,
      }}
    />
  )
}

export function MissionControl({
  summary,
  orders,
  buyerMap,
  loading,
  ordersComplete = false,
  error,
  lastFetchedAt,
  onRefresh,
  onSelect,
  compact = false,
  shippingOrigin = DEFAULT_SHIPPING_ORIGIN,
}: Props) {
  const [returnMonthKey, setReturnMonthKey] = useState<string | null>(null)
  const [shipmentQuery, setShipmentQuery] = useState('')
  const [attentionOnly, setAttentionOnly] = useState(false)
  const [packedOrderIds, setPackedOrderIds] = useState<Set<number>>(() => new Set())
  const [printOrderId, setPrintOrderId] = useState<number | null>(null)
  const [printProvider, setPrintProvider] = useState<PrintProvider>('cdek')
  const [printBundle, setPrintBundle] = useState<PrintBundle | null>(null)
  const [printPdfUrls, setPrintPdfUrls] = useState<{ a6: string; a4?: string } | null>(null)
  const [printPdfBlobs, setPrintPdfBlobs] = useState<{ a6: Blob; a4?: Blob } | null>(null)
  const [printLoading, setPrintLoading] = useState(false)
  const [printError, setPrintError] = useState<string | null>(null)
  const [printMissingFields, setPrintMissingFields] = useState<readonly string[]>([])
  const [packingOrderId, setPackingOrderId] = useState<number | null>(null)
  const [packingKey, setPackingKey] = useState<string | null>(null)

  const closePrintViewer = () => {
    revokePdfUrls(printPdfUrls)
    setPrintOrderId(null)
    setPrintBundle(null)
    setPrintPdfUrls(null)
    setPrintPdfBlobs(null)
    setPrintError(null)
    setPrintMissingFields([])
    setPackingKey(null)
  }

  const openPrintViewer = async (orderId: number, provider: PrintProvider = 'cdek') => {
    revokePdfUrls(printPdfUrls)
    setPrintOrderId(orderId)
    setPrintProvider(provider)
    setPrintBundle(null)
    setPrintPdfUrls(null)
    setPrintPdfBlobs(null)
    setPrintError(null)
    setPrintMissingFields([])
    setPrintLoading(true)
    setPackingKey(provider === 'cdek' ? `mission-control:${orderId}:${Date.now()}` : null)
    try {
      let bundle: PrintBundle
      let a6: Blob
      let a4: Blob | null
      if (provider === 'cdek') {
        const cdekBundle = await fetchCdekPrintDocuments(orderId)
        bundle = cdekBundle
        a6 = await fetchCdekPrintPdf(cdekBundle.a6.url)
        a4 = await fetchCdekPrintPdf(cdekBundle.a4.url)
      } else {
        const yandexBundle = await fetchYandexPrintDocuments(orderId)
        bundle = yandexBundle
        a6 = await fetchYandexPrintPdf(yandexBundle.a6.url)
        a4 = null
      }
      if (typeof URL.createObjectURL !== 'function') {
        throw new Error(`Браузер не поддерживает просмотр документов ${provider === 'cdek' ? 'СДЭК' : 'Маркета'}.`)
      }
      const urls = {
        a6: URL.createObjectURL(a6),
        ...(a4 ? { a4: URL.createObjectURL(a4) } : {}),
      }
      setPrintBundle(bundle)
      setPrintPdfUrls(urls)
      setPrintPdfBlobs({ a6, ...(a4 ? { a4 } : {}) })
    } catch (caught) {
      setPrintError(caught instanceof Error
        ? caught.message
        : `Не удалось открыть документы ${provider === 'cdek' ? 'СДЭК' : 'Маркета'}.`)
      setPrintMissingFields(
        caught instanceof CdekPrintRefusalError || caught instanceof YandexPrintRefusalError
          ? caught.missingFieldLabels
          : [],
      )
    } finally {
      setPrintLoading(false)
    }
  }

  const packCurrentOrder = async () => {
    if (printOrderId === null || packingKey === null) return
    setPackingOrderId(printOrderId)
    setPrintError(null)
    try {
      const result = await packOrder(printOrderId, packingKey)
      if (result.orderId !== printOrderId || result.outcome !== 'packed') {
        throw new Error('Сервер не подтвердил упаковку заказа.')
      }
      setPackedOrderIds((current) => {
        const next = new Set(current)
        next.add(printOrderId)
        return next
      })
      closePrintViewer()
      onRefresh?.()
    } catch (caught) {
      setPrintError(caught instanceof Error ? caught.message : 'Не удалось сохранить упаковку заказа.')
    } finally {
      setPackingOrderId(null)
    }
  }

  const dispatchWindow = nextDispatchWindow(shippingOrigin)
  const dispatchItems = summary?.awaitingHandoff.orders.filter(
    (item) => (item.order.programType || 'DBS') === 'DBS',
  ) || []
  const marketDispatchItems = summary?.awaitingHandoff.orders.filter(
    (item) => (item.order.programType || 'DBS') !== 'DBS',
  ) || []
  const isPacked = (item: MissionControlOrder) =>
    Boolean(item.order.packedAt) || packedOrderIds.has(item.order.orderId)
  const awaitingHandoffCount = dispatchItems.length + marketDispatchItems.length
  // Summed over the same items the count is taken from. Reading the raw
  // queue totals let the tile report money for orders it did not count.
  const awaitingHandoffAmount = knownAmount([...dispatchItems, ...marketDispatchItems])
  const dispatchIds = new Set([...dispatchItems, ...marketDispatchItems].map((item) => item.order.orderId))
  const courierItems = summary?.groups.out_for_delivery.orders || []
  const courierIds = new Set(courierItems.map((item) => item.order.orderId))
  const urgentItems = summary?.urgent.orders || []
  const urgentIds = new Set(urgentItems.map((item) => item.order.orderId))
  const returningItems = summary?.groups.returning.orders || []
  const returningIds = new Set(returningItems.map((item) => item.order.orderId))
  const activeItems = summary ? Object.values(summary.groups)
    .filter((group) => group.stage !== 'awaiting_payment')
    .flatMap((group) => group.orders) : []
  const transitItems = activeItems
    .filter((item) => !dispatchIds.has(item.order.orderId) && !courierIds.has(item.order.orderId) && !returningIds.has(item.order.orderId))
    .sort((left, right) => {
      const leftPlan = left.plannedDeliveryDate ? Date.parse(left.plannedDeliveryDate) : Number.POSITIVE_INFINITY
      const rightPlan = right.plannedDeliveryDate ? Date.parse(right.plannedDeliveryDate) : Number.POSITIVE_INFINITY
      if (leftPlan !== rightPlan) return leftPlan - rightPlan
      return missionRecipientProximity(right) - missionRecipientProximity(left)
    })
  const urgentTransitItems = transitItems.filter((item) => urgentIds.has(item.order.orderId))
  const regularTransitItems = transitItems.filter((item) => !urgentIds.has(item.order.orderId))
  const transitAmount = knownAmount(transitItems)
  const courierAmount = knownAmount(courierItems)
  const shipmentItems = [...urgentTransitItems, ...courierItems, ...regularTransitItems]
  const normalizedShipmentQuery = normalizedSearchValue(shipmentQuery)
  const matchesShipmentFilters = (item: MissionControlOrder) => (
    (!attentionOnly || urgentIds.has(item.order.orderId))
    && (!normalizedShipmentQuery || shipmentSearchValue(item, buyerMap).includes(normalizedShipmentQuery))
  )
  const filteredShipmentItems = shipmentItems.filter(matchesShipmentFilters)
  const filteredReturningItems = returningItems.filter(matchesShipmentFilters)
  const monthlyReturns = useMemo(
    () => returnMonthKey ? returnsForMonth(orders, returnMonthKey) : [],
    [orders, returnMonthKey],
  )
  if (summary === null) {
    return (
      <section className="mx-auto w-full max-w-[1240px] px-4 py-8 sm:px-6 lg:px-10" aria-labelledby="mission-control-title">
        <div className="eyebrow">Оперативная сводка</div>
        <h2 id="mission-control-title" className="mt-2 text-[28px] font-semibold text-ink">Доставка сейчас</h2>
        <div className="mt-6 rounded-xl border border-rule bg-paper px-5 py-8 text-sm text-ink-muted">
          {loading ? 'Загружаем полную сводку…' : 'Полная сводка доставки временно недоступна. Повторите попытку позже.'}
        </div>
      </section>
    )
  }
  const initialLoading = false
  const unavailable = false

  if (compact) {
    return (
      <section className="border-b border-rule bg-ivory px-4 py-3" aria-label="Доставка сейчас">
        <div className="eyebrow">Доставка сейчас</div>
        <div className="mt-2 grid grid-cols-2 border-y border-rule">
          <div className="py-2.5 pr-3">
            <div className="text-[9px] font-medium uppercase tracking-[0.1em] text-ink-muted">К покупателям</div>
            <div className="mt-1 flex items-baseline gap-1.5">
              <span className="text-[24px] font-semibold leading-none tab-nums text-st-delivery">{initialLoading || unavailable ? '—' : summary.buyerBound.count}</span>
              <span className="text-[10px] text-ink-muted">{initialLoading ? 'обновляем' : unavailable ? 'нет данных' : orderWord(summary.buyerBound.count)}</span>
            </div>
            <div className="mt-1 text-[11px] font-semibold tab-nums text-ink-2">{initialLoading ? 'Загружаем…' : <ValueOrReason value={unavailable ? null : flowAmount(summary.buyerBound)} reason={null} />}</div>
          </div>
          <div className="border-l border-rule py-2.5 pl-3">
            <div className="text-[9px] font-medium uppercase tracking-[0.1em] text-st-unpaid">
              <span>Возвращается к нам</span>
            </div>
            <div className="mt-1 flex items-baseline gap-1.5">
              <span className="text-[24px] font-semibold leading-none tab-nums text-st-unpaid">{initialLoading || unavailable ? '—' : summary.returning.count}</span>
              <span className="text-[10px] text-ink-muted">{initialLoading ? 'обновляем' : unavailable ? 'нет данных' : orderWord(summary.returning.count)}</span>
            </div>
            <div className="mt-1 text-[11px] font-semibold tab-nums text-ink-2">{initialLoading ? 'Загружаем…' : <ValueOrReason value={unavailable ? null : flowAmount(summary.returning)} reason={null} />}</div>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3 text-[10px] text-ink-faint">
          <span>{unavailable ? 'повторите попытку позже' : summary.complete ? 'все активные заказы' : 'сводка загружается…'}</span>
          <span className="tab-nums">
            Всего в движении: {initialLoading || unavailable ? '—' : `${summary.totalInMotion.count} · ${flowAmount(summary.totalInMotion)}`}
          </span>
        </div>
        <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1">
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-st-processing/9 px-2.5 py-1 text-[11px] text-st-processing">
            <span className="size-1.5 rounded-full bg-st-processing" />К передаче · {initialLoading || unavailable ? '—' : awaitingHandoffCount}
          </span>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-st-delivery/8 px-2.5 py-1 text-[11px] text-st-delivery">
            <span className="size-1.5 rounded-full bg-st-delivery" />В транзите · {initialLoading || unavailable ? '—' : transitItems.length}
          </span>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-gold/10 px-2.5 py-1 text-[11px] text-gold-deep">
            <span className="size-1.5 rounded-full bg-gold-deep" />У курьера · {initialLoading || unavailable ? '—' : courierItems.length}
          </span>
          {returningItems.length > 0 ? (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-st-unpaid/7 px-2.5 py-1 text-[11px] text-st-unpaid">
              <span className="size-1.5 rounded-full bg-st-unpaid" />Возвращаются · {returningItems.length}
            </span>
          ) : null}
        </div>
      </section>
    )
  }

  return (
    <section className="mx-auto w-full max-w-[1240px] px-4 py-4 sm:px-6 sm:py-5 lg:px-10 lg:py-6" aria-labelledby="mission-control-title">
      <div className="flex items-center justify-between gap-3">
        <h2 id="mission-control-title" className="text-[17px] font-semibold tracking-[-0.02em] text-ink sm:text-[19px]">
          Доставка сейчас
        </h2>
        {onRefresh ? (
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            aria-label="Обновить данные"
            title="Обновить данные"
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-bone hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:cursor-wait disabled:opacity-45"
          >
            <span className={loading ? 'animate-spin motion-reduce:animate-none' : ''}><RefreshIcon /></span>
          </button>
        ) : null}
      </div>

      <div className="mt-3 grid grid-cols-2 border-y border-rule">
        <div className="py-3 pr-4 sm:py-4">
          <div className="text-[10px] font-medium text-ink-muted">К покупателям</div>
          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-[30px] font-semibold leading-none tab-nums text-st-delivery sm:text-[36px]">{initialLoading || unavailable ? '—' : summary.buyerBound.count}</span>
            <span className="text-[12px] font-semibold tab-nums text-ink-2 sm:text-[13px]">{initialLoading ? 'Обновляем…' : unavailable ? 'Нет данных' : flowAmount(summary.buyerBound)}</span>
          </div>
        </div>
        <div className="border-l border-rule py-3 pl-4 sm:py-4">
          <div className="text-[10px] font-medium text-st-unpaid">Возвращается к нам</div>
          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-[30px] font-semibold leading-none tab-nums text-st-unpaid sm:text-[36px]">{initialLoading || unavailable ? '—' : summary.returning.count}</span>
            <span className="text-[12px] font-semibold tab-nums text-ink-2 sm:text-[13px]">{initialLoading ? 'Обновляем…' : unavailable ? 'Нет данных' : flowAmount(summary.returning)}</span>
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-5 gap-y-2 text-[10px] text-ink-faint">
        <span className="font-medium tab-nums text-ink-muted">
          Всего в движении: {initialLoading || unavailable ? '—' : `${summary.totalInMotion.count} · ${flowAmount(summary.totalInMotion)}`}
        </span>
        <span>
          {loading
            ? 'Обновляем данные…'
            : error
              ? 'Сохранена последняя полная сводка'
              : summary.complete
                ? lastFetchedAt ? formatRelativeTime(lastFetchedAt) : 'Данные актуальны'
                : 'Полная сводка недоступна'}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {summary.urgent.count > 0 ? (
          <button
            type="button"
            aria-pressed={attentionOnly}
            onClick={() => setAttentionOnly((active) => !active)}
            className={`inline-flex min-h-8 items-center gap-2 rounded-full px-3 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-st-unpaid ${attentionOnly ? 'bg-st-unpaid text-ivory' : 'bg-st-unpaid/7 text-st-unpaid ring-1 ring-inset ring-st-unpaid/25 hover:bg-st-unpaid/11'}`}
          >
            <span className={`size-1.5 rounded-full ${attentionOnly ? 'bg-ivory' : 'bg-st-unpaid'}`} />
            Требуют внимания {summary.urgent.count}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setReturnMonthKey(currentMonthKeyInRiga())}
          className="min-h-8 rounded-full border border-rule-strong bg-paper px-3 text-[11px] font-medium text-ink-2 transition-colors hover:border-st-unpaid/45 hover:bg-st-unpaid/5 hover:text-st-unpaid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-st-unpaid"
        >
          Все возвраты за месяц
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 border-t border-rule/70 pt-2 text-[10px] text-ink-muted">
        <span>К передаче <strong className="font-semibold tab-nums text-ink-2">{awaitingHandoffCount}</strong> <span className="tab-nums">{money(awaitingHandoffAmount)}</span></span>
        <span>В транзите <strong className="font-semibold tab-nums text-ink-2">{transitItems.length}</strong> <span className="tab-nums">{money(transitAmount)}</span></span>
        <span>Финишная прямая <strong className="font-semibold tab-nums text-ink-2">{courierItems.length}</strong> <span className="tab-nums">{money(courierAmount)}</span></span>
      </div>

      {error && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-st-unpaid/7 px-4 py-3.5 text-[12px] ring-1 ring-st-unpaid/25">
          <div>
            <div className="font-semibold text-st-unpaid">Не удалось обновить полную сводку</div>
            <div className="mt-0.5 text-ink-muted">Сохранена последняя полная сводка; повторите попытку позже.</div>
          </div>
          {onRefresh && (
            <button type="button" onClick={onRefresh} className="btn btn-sm btn-ghost">
              Повторить
            </button>
          )}
        </div>
      )}

      {summary.potential.count > 0 && (
        <div className="mt-2 text-[10px] text-ink-muted">
          В корзине покупателей — {summary.potential.count} · до {money(summary.potential.knownAmount)}
        </div>
      )}

      {dispatchItems.length > 0 && (
        <div className="mt-8 border-y border-rule bg-paper/60 px-4 py-5 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h3 className="text-[17px] font-semibold text-ink">Нужно передать в СДЭК · {dispatchItems.length}</h3>
            <div className="min-w-[220px] text-left sm:text-right">
              <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-muted">Ближайший рейс</div>
              <div className="mt-1 text-[18px] font-semibold leading-tight text-gold-deep sm:text-[20px]">
                {dispatchWindowLabel(dispatchWindow)}
              </div>
              <div className="mt-1 text-[11px] text-ink-muted">
                {shippingOrigin.city}, {shippingTimezoneLabel(shippingOrigin.timezone)}
              </div>
            </div>
          </div>
          <div
            className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6"
            role="region"
            aria-label="Заказы к передаче в СДЭК"
          >
            {dispatchItems.map((item) => (
              <DispatchCard
                key={item.order.orderId}
                item={item}
                buyerMap={buyerMap}
                onSelect={onSelect}
                onPrint={(orderId) => void openPrintViewer(orderId, 'cdek')}
                printing={printLoading && printOrderId === item.order.orderId}
                packed={isPacked(item)}
                dispatchDate={dispatchWindow?.localDate}
              />
            ))}
          </div>
        </div>
      )}

      {marketDispatchItems.length > 0 && (
        <div className="mt-8 border-y border-rule bg-paper/60 px-4 py-5 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h3 className="text-[17px] font-semibold text-ink">Отвезти на СЦ Маркета — Кемерово · {marketDispatchItems.length}</h3>
              <p className="mt-1 text-[12px] text-ink-muted">FBS-заказы Яндекс доставит покупателю сам</p>
            </div>
            <div className="text-right text-[13px] font-semibold tab-nums text-ink-2">
              {money(knownAmount(marketDispatchItems))}
            </div>
          </div>
          {marketDispatchDays(marketDispatchItems).map((day) => (
            <div key={day.key || 'no-date'} className="mt-4">
              <h4 className="text-[13px] font-semibold text-ink-2">
                {day.label ? `Отгрузка ${day.label} · ${day.items.length}` : `Дата отгрузки не указана · ${day.items.length}`}
              </h4>
              <div
                className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6"
                role="region"
                aria-label={day.label ? `Заказы к отгрузке на СЦ Маркета ${day.label}` : 'Заказы к отгрузке на СЦ Маркета без даты'}
              >
                {day.items.map((item) => (
                  <DispatchCard
                    key={item.order.orderId}
                    item={item}
                    buyerMap={buyerMap}
                    onSelect={onSelect}
                    onPrint={(orderId) => void openPrintViewer(orderId, 'ymarket')}
                    printing={printLoading && printOrderId === item.order.orderId}
                    dispatchDate={item.order.delivery?.shipment?.shipmentDate}
                    dispatchLabel="Отгрузка на СЦ"
                    packed={isPacked(item)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredReturningItems.length > 0 && (
        <div className="mt-8 border-y border-rule bg-paper/60 px-4 py-5 sm:px-6">
          <h3 className="text-[17px] font-semibold text-ink">Возвраты · {filteredReturningItems.length}</h3>
          <div
            className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6"
            role="list"
            aria-label="Возвраты"
          >
            {filteredReturningItems.map((item) => (
              <div key={item.order.orderId} role="listitem">
                <ShipmentLine item={item} buyerMap={buyerMap} onSelect={onSelect} />
              </div>
            ))}
          </div>
        </div>
      )}

      {!unavailable ? (
        <div id="shipment-list" className="mt-8 scroll-mt-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-[15px] font-semibold text-ink">Заказы в движении</h3>
            <div className="text-[11px] text-ink-muted" aria-live="polite">
              Показано {filteredShipmentItems.length} из {shipmentItems.length}
            </div>
          </div>

          <div className="mt-3">
            <ShipmentSearch value={shipmentQuery} onChange={setShipmentQuery} />
          </div>

          <div id="mission-shipment-results">
            {filteredShipmentItems.length > 0 ? (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6" role="list" aria-label="Заказы в движении">
                {filteredShipmentItems.map((item) => (
                  <div key={item.order.orderId} role="listitem">
                    <ShipmentLine item={item} buyerMap={buyerMap} onSelect={onSelect} />
                  </div>
                ))}
              </div>
            ) : loading && shipmentItems.length === 0 ? (
              <div className="mt-3 border-y border-rule bg-paper px-5 py-7" role="status" aria-label="Обновляем заказы">
                <div className="flex items-center justify-center gap-2 text-[14px] font-medium text-ink">
                  <span className="animate-spin text-gold-deep motion-reduce:animate-none"><RefreshIcon /></span>
                  Обновляем заказы…
                </div>
                <div className="mx-auto mt-5 grid max-w-[920px] gap-3" aria-hidden="true">
                  {[72, 88, 64].map((width) => (
                    <div key={width} className="animate-pulse border-b border-rule/70 pb-3 motion-reduce:animate-none">
                      <div className="h-2.5 rounded-full bg-bone" style={{ width: `${width}%` }} />
                      <div className="mt-2 h-2 rounded-full bg-bone/80" style={{ width: `${Math.max(38, width - 24)}%` }} />
                    </div>
                  ))}
                </div>
              </div>
            ) : shipmentItems.length === 0 ? (
              <div className="mt-3 border-y border-rule bg-paper px-5 py-8 text-center">
                <div className="text-[14px] font-medium text-ink">Сейчас нет заказов в пути</div>
                <div className="mt-1 text-[12px] text-ink-muted">Новые статусы появятся здесь после обновления СДЭК.</div>
              </div>
            ) : (
              <div className="mt-3 border-y border-rule bg-paper px-5 py-8 text-center">
                <div className="text-[14px] font-medium text-ink">По этому запросу заказов нет</div>
                <div className="mt-1 text-[12px] text-ink-muted">
                  {attentionOnly ? 'Измените запрос или отключите фильтр «Требуют внимания».' : 'Измените запрос.'}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {returnMonthKey && (
        <MonthlyReturnsDialog
          items={monthlyReturns}
          monthKey={returnMonthKey}
          buyerMap={buyerMap}
          complete={ordersComplete}
          onClose={() => setReturnMonthKey(null)}
          onSelect={onSelect}
        />
      )}

      {printOrderId !== null && (
        <PrintViewer
          key={`${printProvider}:${printOrderId}`}
          orderId={printOrderId}
          provider={printProvider}
          documents={printBundle}
          pdfUrls={printPdfUrls}
          pdfBlobs={printPdfBlobs}
          loading={printLoading}
          error={printError}
          missingFields={printMissingFields}
          packing={packingOrderId === printOrderId}
          onClose={closePrintViewer}
          onPack={packCurrentOrder}
          onRetry={() => openPrintViewer(printOrderId, printProvider)}
        />
      )}


    </section>
  )
}
